import type { MouseEvent } from 'react';
import React from 'react';
import clsx from 'clsx';
import { debounce } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';
import {
  WaveFormCommentsContainer,
  WaveformPlaceholder,
} from '@components/Waveform/WaveformStyles';
import { paintCanvas } from '@components/Waveform/paintCanvas';
import waveformAvgChunker from '@components/Waveform/waveformAvgChunker';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType, WaveformCommentsReleaseInfoFragmentDoc } from '@graphql/reactQuery';
import { loadTrack, seek, useAudioController } from '@store/audio/AudioController';
import { play } from '@store/audio/AudioEngineHTML5';
import { useAudioPosition } from '@store/audio/AudioPosition';
import { WaveformComments } from '../WaveformComments';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment WaveformInfo on Release {
    id
    track {
      id
      normalizedPeaks
      duration
    }
    ...WaveformCommentsReleaseInfo
  }
`);

const PLAYED_COLOR = 'hsla(0, 100%, 100%, 1)';
const UNPLAYED_COLOR = 'hsla(0, 100%, 100%, 0.4)';
const HOVER_COLOR = 'hsla(0, 100%, 100%, 0.6)';
const BOTTOM_HALF_COLOR = 'hsla(0, 0%, 0%, 0.2)';

// Represents the ratio of the top half of the waveform to the bottom half
const TOP_WAVE_HEIGHT_MOBILE = 2 / 3;
const TOP_WAVE_HEIGHT_DESKTOP = 3 / 4;

const WAVEFORM_HEIGHT_MOBILE = 48;
const WAVEFORM_HEIGHT_DESKTOP = 48;
const WAVEFORM_HEIGHT_NO_BOTTOM_MOBILE = 20;
const WAVEFORM_HEIGHT_NO_BOTTOM_DESKTOP = 40;

const WAVEFORM_BAR_WIDTH = 2;
const WAVEFORM_BAR_MARGIN = 2;

// This is necessary so that the rendered peak are not blurry
const RESOLUTION_MULTIPLIER = 4;

export type WaveformTrack = {
  id: string;
  duration: number;
  normalizedPeaks?: number[];
};

export function Waveform({
  track,
  isTrackDisabled,
  showComments,
  showBottomHalf,
  enableTouchHandler,
  ...props
}: {
  track: WaveformTrack;
  isTrackDisabled: boolean;
  showComments: boolean;
  showBottomHalf: boolean;
  enableTouchHandler: boolean;
} & (
  | {
      audioSrc: string;
    }
  | {
      releaseFrag: FragmentType<typeof WaveformCommentsReleaseInfoFragmentDoc>;
    }
)) {
  const audioSrc = 'audioSrc' in props ? props.audioSrc : undefined;
  const releaseFrag = 'releaseFrag' in props ? props.releaseFrag : undefined;

  const { tablet } = useMediaQuery();

  const { ready, activeTrackId, playing } = useAudioController();
  const [waveformWidth, setWaveformWidth] = React.useState(0);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [hoverXCoord, setHoverXCoord] = React.useState<number>(0);
  const [seekSec, setSeekSec] = React.useState<number>();

  const playedColor = PLAYED_COLOR;
  const unPlayedColor = UNPLAYED_COLOR;

  let height: number;
  if (showBottomHalf) {
    height = tablet ? WAVEFORM_HEIGHT_DESKTOP : WAVEFORM_HEIGHT_MOBILE;
  } else {
    height = tablet ? WAVEFORM_HEIGHT_NO_BOTTOM_DESKTOP : WAVEFORM_HEIGHT_NO_BOTTOM_MOBILE;
  }

  const trackDuration = track.duration;

  const isDisabled = isTrackDisabled;

  const { percentComplete } = useAudioPosition();

  const isActiveTrack = audioSrc ? true : track.id === activeTrackId;

  const trackPerc = isDisabled ? 0 : isActiveTrack ? percentComplete / 100 : 0;
  const playingBarNum = Math.floor(
    (trackPerc * waveformWidth) / (WAVEFORM_BAR_WIDTH + WAVEFORM_BAR_MARGIN),
  );

  const chunkedPeaks = React.useMemo(() => {
    const normalizedPeaks = track.normalizedPeaks || [];

    const numBars = Math.floor(waveformWidth / (WAVEFORM_BAR_WIDTH + WAVEFORM_BAR_MARGIN));
    return waveformAvgChunker(normalizedPeaks, numBars);
  }, [track.normalizedPeaks, waveformWidth]);

  const paintWaveformCanvas = React.useCallback(() => {
    if (!canvasRef.current) {
      return;
    }
    paintCanvas({
      canvasRef,
      normalizedPeaks: chunkedPeaks,
      maxBarHeight: height * RESOLUTION_MULTIPLIER,
      barWidth: WAVEFORM_BAR_WIDTH * RESOLUTION_MULTIPLIER,
      barMargin: WAVEFORM_BAR_MARGIN * RESOLUTION_MULTIPLIER,
      playingBarNum,
      hoverXCoord: hoverXCoord * RESOLUTION_MULTIPLIER,
      isActiveTrack,
      unPlayedColor,
      playedColor,
      showBottomHalf,
      gapHeight: -1, // prevents gap between top and bottom half of waveform
      bottomHalfColor: BOTTOM_HALF_COLOR,
      topHalfHeight: showBottomHalf
        ? tablet
          ? TOP_WAVE_HEIGHT_DESKTOP
          : TOP_WAVE_HEIGHT_MOBILE
        : 1,
      bottomHalfHeight: showBottomHalf
        ? 1 - (tablet ? TOP_WAVE_HEIGHT_DESKTOP : TOP_WAVE_HEIGHT_MOBILE)
        : undefined,
      hoverColor: HOVER_COLOR,
    });
  }, [
    chunkedPeaks,
    height,
    playingBarNum,
    hoverXCoord,
    isActiveTrack,
    unPlayedColor,
    playedColor,
    tablet,
    showBottomHalf,
  ]);

  React.useEffect(() => {
    paintWaveformCanvas();
  }, [paintWaveformCanvas]);

  const setDefaultX = React.useCallback(() => {
    setHoverXCoord(0);
  }, []);

  React.useEffect(() => {
    // resets the hoverstate when user changes program or tab after hovering
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setDefaultX();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [setDefaultX]);

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (isDisabled) {
        return;
      }
      // debounced hover coord updates
      requestAnimationFrame(() => {
        if (!canvasRef.current) {
          return;
        }
        setHoverXCoord(e.clientX - canvasRef.current.getBoundingClientRect().left);
      });
    },
    [isDisabled],
  );

  // Loads the audio which will replace the current track if different
  // and then sets the seekSec state
  // After the track is ready, the useEffect will seek the track to seekSec
  const seekToSec = React.useCallback(
    (sec: number) => {
      if (isDisabled) return;
      if (!!audioSrc) {
        loadTrack({
          audioSrc: audioSrc,
        });
      } else {
        loadTrack({ trackId: track.id });
      }
      setSeekSec(sec);
    },
    [audioSrc, isDisabled, track.id],
  );
  // Applies and clears seekSec after load
  React.useEffect(() => {
    if (isActiveTrack && seekSec && ready) {
      seek(seekSec);
      if (!playing) {
        play();
      }
      setSeekSec(undefined);
    }
  }, [isActiveTrack, ready, seekSec, playing]);

  const seekTrack: React.MouseEventHandler<HTMLCanvasElement> = React.useCallback(
    e => {
      if (!canvasRef.current || isDisabled) {
        return;
      }

      const xCoord = e.clientX - canvasRef.current.getBoundingClientRect().left;
      const seekPerc = xCoord / waveformWidth;
      seekToSec(Math.floor(trackDuration * seekPerc));
    },
    [isDisabled, seekToSec, trackDuration, waveformWidth],
  );

  const seekTrackOnTouch: React.TouchEventHandler<HTMLCanvasElement> = React.useCallback(
    e => {
      if (!canvasRef.current || isDisabled) {
        return;
      }
      const clientX = e.touches[0]?.clientX ?? 0;
      const xCoord = clientX - canvasRef.current.getBoundingClientRect().left;
      const seekPerc = xCoord / waveformWidth;
      seekToSec(Math.floor(trackDuration * seekPerc));
    },
    [isDisabled, seekToSec, trackDuration, waveformWidth],
  );

  const handleTouchEnd: React.TouchEventHandler<HTMLCanvasElement> = React.useCallback(e => {
    e.preventDefault();
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setWaveformWidth(containerRef.current.offsetWidth);
      }
    };
    const debouncedHandleResize = debounce(handleResize, 200);

    window.addEventListener('resize', debouncedHandleResize);
    handleResize();

    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [containerRef]);

  return (
    <>
      <div
        ref={containerRef}
        className={clsx(
          'relative w-full',
          showBottomHalf ? 'h-12 pt-4 md:h-12 md:pt-3' : 'h-5 md:h-10',
        )}
      >
        <canvas
          style={{
            display: 'block',
            cursor: isDisabled ? 'default' : 'pointer',
            backgroundColor: 'transparent',
            WebkitTapHighlightColor: 'transparent',
            width: '100%',
            height,
          }}
          ref={canvasRef}
          width={waveformWidth * RESOLUTION_MULTIPLIER}
          height={height * RESOLUTION_MULTIPLIER}
          onBlur={setDefaultX}
          onMouseOut={setDefaultX}
          onMouseMove={handleMouseMove}
          onClick={seekTrack}
          onTouchStart={enableTouchHandler ? seekTrackOnTouch : undefined}
          onTouchEnd={enableTouchHandler ? handleTouchEnd : undefined}
        />
        <WaveformPlaceholder />
      </div>
      {showComments && (
        <WaveFormCommentsContainer>
          {!!releaseFrag && (
            <WaveformComments seekToSec={seekToSec} width={waveformWidth} release={releaseFrag} />
          )}
        </WaveFormCommentsContainer>
      )}
    </>
  );
}
